import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Typography,
    Dialog,
    AppBar,
    Toolbar,
    IconButton,
    Slide,
    Box,
    ButtonGroup,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/RemoveCircleOutline';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import moment from 'moment';
import FileUpload from '@components/FileUpload';
import { createRoomBills } from '@utilities/auth';
import {
    buildRoomBillSnapshot,
    logOrderEventAsync,
} from '@utilities/orderLogging';
import { fetchRoomBillById } from '@utilities/order';
import { PaymentTypes } from '@src/constants/common';
import { generateUniqueCode, hasPermission } from '@utilities/common';
import { useOnlineStatus } from '@src/app/hooks';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/** Matches getFormattedDate / bill display: day of month, not day of week. */
const DATE_TIME_PICKER_FORMAT = 'DD MMM, YYYY - hh:mm a';

/** Current local date/time as ISO string for new guest check-in. */
const getDefaultCheckInDate = () => dayjs().toISOString();

/** Normalize Firestore / API date values for the date pickers. */
const parseBillDate = (value: unknown): string | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    if (typeof value === 'number') {
        const fromMs = dayjs(value);
        return fromMs.isValid() ? fromMs.toISOString() : null;
    }
    if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
        const fromTimestamp = dayjs((value as { toDate: () => Date }).toDate());
        return fromTimestamp.isValid() ? fromTimestamp.toISOString() : null;
    }
    const parsed = dayjs(value as string | Date);
    return parsed.isValid() ? parsed.toISOString() : null;
};

type RoomFormRow = {
    roomType: string;
    roomCost: string;
    roomCustomCost: string;
    noOfNights: number;
    roomNumber: string;
};

/** First room type from restaurant config (same order as the Room Type dropdown). */
const buildDefaultRoomRow = (
    restaurant: { roomType?: Record<string, string> } | null | undefined,
    opts?: { roomNumber?: string },
): RoomFormRow => {
    const row: RoomFormRow = {
        roomType: '',
        roomCost: '',
        roomCustomCost: '',
        noOfNights: 1,
        roomNumber: opts?.roomNumber ?? '',
    };
    const roomTypeMap = restaurant?.roomType;
    if (!roomTypeMap || typeof roomTypeMap !== 'object') {
        return row;
    }
    const entries = Object.entries(roomTypeMap);
    if (entries.length === 0) {
        return row;
    }
    const [costKey, typeLabel] = entries[0];
    return {
        ...row,
        roomCost: costKey,
        roomCustomCost: costKey,
        roomType: String(typeLabel),
    };
};

const getInitialFormState = () => ({
    uoId: '',
    guestName: '',
    guestMobile: '',
    guestAddress: '',
    guestOther: '',
    guestExtraInfo: '',
    guestCompanyName: '',
    guestCompanyGST: '',
    checkInDate: getDefaultCheckInDate(),
    checkOutDate: null as string | null,
    discount: 0,
    advancePayment: 0,
    selectedPayment: 'cash',
    rooms: [buildDefaultRoomRow(null)],
    identificationFiles: [] as string[],
});

const InitialFormErrorState = {
    checkInDate: '',
    checkOutDate: '',
    roomType: '',
    noOfNights: '',
    guestName: '',
};

export type RoomBillingGuestDialogProps = {
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
    editOrderId: string | null;
    orderSnapshot?: Record<string, unknown> | null;
    prefilledRoomNumber?: string;
    restaurant: any;
    user: any;
    roomList: { label: string; value: string }[];
    roomGSTSlab: any;
    userPermissions: any;
};

const RoomBillingGuestDialog: React.FC<RoomBillingGuestDialogProps> = ({
    open,
    onClose,
    onSaved,
    editOrderId,
    orderSnapshot,
    prefilledRoomNumber,
    restaurant,
    user,
    roomList,
    roomGSTSlab,
    userPermissions,
}) => {
    const [formData, setFormData] = useState(() => getInitialFormState());
    const [showError, setShowError] = useState({ ...InitialFormErrorState });
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
    const [disableSaveBtn, setDisableSaveBtn] = useState(false);
    const [loadingBill, setLoadingBill] = useState(false);
    const [checked] = useState(true);
    const stableUoIdRef = useRef<string | null>(null);
    const prevBillRef = useRef<any>(null);

    const params = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isOnline = useOnlineStatus();
    const canUploadGuestImages = hasPermission(userPermissions, 'uploadGuestImages', 'write');

    const resetForm = useCallback((opts?: { roomNumber?: string; preserveUoId?: string }) => {
        const base = getInitialFormState();
        const uoId = opts?.preserveUoId || stableUoIdRef.current || generateUniqueCode(6);
        stableUoIdRef.current = uoId;
        setFormData({
            ...base,
            uoId,
            checkInDate: getDefaultCheckInDate(),
            checkOutDate: null,
            rooms: [buildDefaultRoomRow(restaurant, opts?.roomNumber ? { roomNumber: opts.roomNumber } : undefined)],
        });
        setShowError({ ...InitialFormErrorState });
        setSelectedOrderId(null);
        setDisableSaveBtn(false);
        prevBillRef.current = null;
    }, [restaurant]);

    useEffect(() => {
        if (!open) {
            return;
        }
        if (editOrderId) {
            let cancelled = false;
            setLoadingBill(true);
            fetchRoomBillById(editOrderId)
                .then((fresh) => {
                    if (cancelled) return;
                    const source: any = fresh ?? orderSnapshot ?? {};
                    prevBillRef.current = { ...source, orderId: editOrderId };
                    const parsedCheckIn = parseBillDate(source?.checkInDate);
                    const parsedCheckOut = parseBillDate(source?.checkOutDate);
                    setFormData({
                        ...getInitialFormState(),
                        ...source,
                        checkInDate: parsedCheckIn ?? getDefaultCheckInDate(),
                        checkOutDate: parsedCheckOut,
                        selectedPayment: source?.selectedPayment ?? 'cash',
                        advancePayment: source?.advancePayment ?? 0,
                    });
                    if (source?.uoId) {
                        stableUoIdRef.current = String(source.uoId);
                    }
                    setSelectedOrderId(editOrderId);
                })
                .finally(() => {
                    if (!cancelled) setLoadingBill(false);
                });
            return () => {
                cancelled = true;
            };
        }
        resetForm(prefilledRoomNumber ? { roomNumber: prefilledRoomNumber } : undefined);
        return undefined;
    }, [open, editOrderId, orderSnapshot, prefilledRoomNumber, resetForm]);

    const handleClose = useCallback(() => {
        resetForm();
        onClose();
    }, [onClose, resetForm]);

    const getGstRateFromSlab = useCallback((amount: number): number => {
        if (!checked) return 0;
        if (typeof amount !== 'number' || isNaN(amount) || !isFinite(amount) || amount < 0) {
            return 0;
        }
        const slab: any = roomGSTSlab;
        let resolvedRate = 0;
        if (Array.isArray(slab)) {
            for (const band of slab) {
                const minAmount = typeof band?.minAmount === 'number' ? band.minAmount : NaN;
                const rate = typeof band?.rate === 'number' ? band.rate : NaN;
                if (!isNaN(minAmount) && !isNaN(rate) && amount >= minAmount) {
                    resolvedRate = rate;
                }
            }
            return resolvedRate;
        }
        if (slab && typeof slab === 'object') {
            const thresholds = Object.keys(slab)
                .map((k) => Number(k))
                .filter((n) => !isNaN(n) && amount >= n)
                .sort((a, b) => a - b);
            if (thresholds.length > 0) {
                const highest = thresholds[thresholds.length - 1];
                resolvedRate = Number(slab[highest]);
                if (isNaN(resolvedRate)) {
                    resolvedRate = 0;
                }
            }
            return resolvedRate;
        }
        return typeof restaurant?.roomGST === 'number' ? restaurant.roomGST : 0;
    }, [checked, roomGSTSlab, restaurant?.roomGST]);

    const handleChange = useCallback((event: any, key?: number) => {
        const { name, value } = event.target;
        if (name === 'roomCost' && typeof key === 'number') {
            setFormData((prevData: any) => {
                const tempRooms = [...prevData.rooms];
                tempRooms[key].roomCost = value;
                tempRooms[key].roomCustomCost = value;
                tempRooms[key].roomType = restaurant?.roomType?.[value];
                return { ...prevData, rooms: tempRooms };
            });
        } else if (name === 'roomNumber' && typeof key === 'number') {
            setFormData((prevData: any) => {
                const tempRooms = [...prevData.rooms];
                tempRooms[key].roomNumber = value;
                return { ...prevData, rooms: tempRooms };
            });
        } else {
            setFormData((prevData: any) => ({ ...prevData, [name]: value }));
        }
    }, [restaurant?.roomType]);

    const handleDateChange = useCallback((value: dayjs.Dayjs | null, name: string) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value && dayjs(value).isValid() ? dayjs(value).toISOString() : null,
        }));
    }, []);

    const checkInPickerValue = useMemo(() => {
        const parsed = dayjs(formData.checkInDate);
        return parsed.isValid() ? parsed : dayjs();
    }, [formData.checkInDate]);

    const checkOutPickerValue = useMemo(() => {
        if (!formData.checkOutDate) {
            return null;
        }
        const parsed = dayjs(formData.checkOutDate);
        return parsed.isValid() ? parsed : null;
    }, [formData.checkOutDate]);

    const addRooms = useCallback(() => {
        setFormData((prevData: any) => ({
            ...prevData,
            rooms: [...prevData.rooms, buildDefaultRoomRow(restaurant)],
        }));
    }, [restaurant]);

    const removeRooms = useCallback((roomIndex: number) => {
        setFormData((prevData: any) => ({
            ...prevData,
            rooms: prevData.rooms.filter((_: any, key: number) => key !== roomIndex),
        }));
    }, []);

    const handleUpdateRooms = useCallback((e: React.ChangeEvent<HTMLInputElement>, key: number) => {
        const { name, value } = e.target;
        setFormData((prevData: any) => {
            const tempRooms = [...prevData.rooms];
            tempRooms[key][name] = value;
            return { ...prevData, rooms: tempRooms };
        });
    }, []);

    const handleFileUpload = useCallback((urls: string[]) => {
        setFormData((prev: any) => ({
            ...prev,
            identificationFiles: [...(prev.identificationFiles || []), ...urls].slice(0, 4),
        }));
    }, []);

    const handleFileRemove = useCallback((url: string) => {
        setFormData((prev: any) => ({
            ...prev,
            identificationFiles: (prev.identificationFiles || []).filter((f: string) => f !== url),
        }));
    }, []);

    const handleSubmit = useCallback((event: any) => {
        event.preventDefault();
        const { roomGST, ownerId, gstIn, roomService, name, foodType, address, docId } = restaurant;
        const { email, phoneNumber, uid } = user;

        const itemsCost = formData.rooms?.reduce(
            (sum: number, obj: any) => sum + (obj?.roomCustomCost * obj?.noOfNights || 0),
            0,
        );
        const discountedAmount = Math.max(itemsCost - (+formData.discount || 0), 0);
        const totalNightsSpent = formData.rooms?.reduce(
            (sum: number, obj: any) => sum + (+obj?.noOfNights || 0),
            0,
        );
        const amountPerNight = totalNightsSpent > 0 ? discountedAmount / totalNightsSpent : discountedAmount;
        const gstRateRaw = getGstRateFromSlab(amountPerNight);
        const gstRate: number =
            typeof gstRateRaw === 'number' && !isNaN(gstRateRaw) && isFinite(gstRateRaw) ? gstRateRaw : 0;
        const gstBill: number = Number(((discountedAmount * gstRate) / 100).toFixed(2));
        const serviceBill: number =
            checked && roomService ? Number(((discountedAmount * roomService) / 100).toFixed(2)) : 0;
        const totalCost: number = discountedAmount + gstBill + serviceBill;

        if (formData.guestName) {
            setDisableSaveBtn(true);
            try {
                const { orderId: _omitOrderId, ...formWithoutOrderId } = formData as any;
                const billPayload: any = {
                    ...formWithoutOrderId,
                    restaurantId: docId,
                    uoId: !selectedOrderId ? generateUniqueCode(6) : formData.uoId,
                    userId: uid,
                    isGstBill: checked,
                    selectedPayment: formData.selectedPayment || 'cash',
                    advancePayment: Math.max(0, Number(formData.advancePayment) || 0),
                    itemsCost,
                    gstBill,
                    gstRate,
                    serviceBill,
                    totalCost,
                    identificationFiles: formData.identificationFiles || [],
                    restaurant: {
                        roomGST: checked && roomGST ? roomGST : 0,
                        ownerId,
                        gstIn: checked ? gstIn : '*******',
                        roomService: checked && roomService ? roomService : 0,
                        name,
                        foodType,
                        address,
                        docId,
                    },
                    user: { email, phoneNumber, uid },
                };
                if (selectedOrderId) {
                    billPayload.orderId = selectedOrderId;
                    billPayload.updatedAt = moment().utc().valueOf();
                } else {
                    billPayload.createdAt = moment().utc().valueOf();
                }
                const previous = selectedOrderId
                    ? buildRoomBillSnapshot(prevBillRef.current || {})
                    : null;
                const next = buildRoomBillSnapshot(billPayload);
                const clientRequestId =
                    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                        ? crypto.randomUUID()
                        : `room-upd-${Date.now()}`;

                createRoomBills(
                    billPayload,
                    dispatch,
                    navigate,
                    params?.restaurantId,
                ).then((result) => {
                    if (selectedOrderId) {
                        const ok = result !== false;
                        logOrderEventAsync({
                            restaurantId: String(docId || params?.restaurantId || 'unknown'),
                            orderId: selectedOrderId,
                            collectionName: 'room-bills',
                            orderKind: 'room',
                            action: 'update',
                            status: ok ? 'success' : 'failed',
                            source: 'room-billing',
                            errorMessage: ok ? null : 'Failed to update room bill',
                            previous,
                            next,
                            snapshot: next,
                            clientRequestId,
                        });
                        if (!ok) {
                            setDisableSaveBtn(false);
                            return;
                        }
                    }
                    onSaved();
                    handleClose();
                });
            } catch {
                setDisableSaveBtn(false);
            }
        } else {
            setShowError({
                checkInDate: formData.checkInDate ? '' : 'Required field.',
                checkOutDate: formData.checkOutDate ? '' : 'Required field.',
                roomType: '',
                noOfNights: '',
                guestName: 'Required field.',
            });
        }
    }, [
        formData,
        restaurant,
        user,
        selectedOrderId,
        checked,
        getGstRateFromSlab,
        dispatch,
        navigate,
        params?.restaurantId,
        onSaved,
        handleClose,
    ]);

    const roomTypeMenuItems = useMemo(() => {
        if (!restaurant?.roomType) return null;
        return Object.entries(restaurant.roomType).map(([value, label]: [string, any]) => (
            <MenuItem key={value} value={value}>{`${label}-${value}`}</MenuItem>
        ));
    }, [restaurant?.roomType]);

    const roomNumberMenuItems = useMemo(
        () =>
            roomList.map((room) => (
                <MenuItem key={room.value} value={room.value}>
                    {room.label}
                </MenuItem>
            )),
        [roomList],
    );

    if (!open) {
        return null;
    }

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            keepMounted={false}
        >
            <AppBar sx={{ position: 'relative' }}>
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                        {loadingBill ? 'Loading guest…' : 'Add Guest Information'}
                    </Typography>
                    <Button variant="contained" color="primary" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        disabled={disableSaveBtn || loadingBill}
                        variant="contained"
                        color="primary"
                        type="submit"
                        onClick={handleSubmit}
                    >
                        Save
                    </Button>
                </Toolbar>
            </AppBar>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid className="dialogWrap" container spacing={2} padding={2}>
                    <Grid item xs={12}>
                        <Typography flexGrow={2} variant="h6">
                            Guest Details
                        </Typography>
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <TextField
                            error={!!showError.guestName}
                            fullWidth
                            label="Guest Name"
                            name="guestName"
                            value={formData.guestName}
                            onChange={handleChange}
                            helperText={showError.guestName}
                            disabled={loadingBill}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <TextField
                            fullWidth
                            label="Mobile"
                            name="guestMobile"
                            value={formData.guestMobile}
                            onChange={handleChange}
                            type="number"
                            disabled={loadingBill}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <TextField
                            fullWidth
                            label="Guest Address"
                            name="guestAddress"
                            value={formData.guestAddress}
                            onChange={handleChange}
                            disabled={loadingBill}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <TextField
                            fullWidth
                            label="Other Guests"
                            name="guestOther"
                            value={formData.guestOther}
                            onChange={handleChange}
                            disabled={loadingBill}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <TextField
                            fullWidth
                            label="Extra Information"
                            name="guestExtraInfo"
                            value={formData.guestExtraInfo}
                            onChange={handleChange}
                            disabled={loadingBill}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <TextField
                            fullWidth
                            label="Guest Company Name"
                            name="guestCompanyName"
                            value={formData.guestCompanyName}
                            onChange={handleChange}
                            disabled={loadingBill}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <TextField
                            fullWidth
                            label="Guest Company GST"
                            name="guestCompanyGST"
                            value={formData.guestCompanyGST}
                            onChange={handleChange}
                            disabled={loadingBill}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                        <TextField
                            fullWidth
                            label="Any Discount"
                            type="number"
                            name="discount"
                            value={formData.discount}
                            onChange={handleChange}
                            disabled={loadingBill}
                            InputProps={{ inputProps: { min: 0, step: 1 } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                        <TextField
                            fullWidth
                            label="Advance Payment"
                            type="number"
                            name="advancePayment"
                            value={formData.advancePayment}
                            onChange={handleChange}
                            disabled={loadingBill}
                            InputProps={{ inputProps: { min: 0, step: 1 } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                        <FormControl fullWidth>
                            <InputLabel id="room-billing-payment-mode-label">Payment mode</InputLabel>
                            <Select
                                labelId="room-billing-payment-mode-label"
                                label="Payment mode"
                                name="selectedPayment"
                                value={formData.selectedPayment || 'cash'}
                                onChange={(e) => handleChange(e)}
                                disabled={loadingBill}
                            >
                                {PaymentTypes.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid container item xs={12} spacing={2}>
                        <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                                Check-in Date &amp; Time
                            </Typography>
                            <DateTimePicker
                                format={DATE_TIME_PICKER_FORMAT}
                                value={checkInPickerValue}
                                onChange={(value) => handleDateChange(value, 'checkInDate')}
                                disabled={loadingBill}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small',
                                    },
                                }}
                                sx={{ width: '100%' }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                                Check-out Date &amp; Time
                            </Typography>
                            <DateTimePicker
                                format={DATE_TIME_PICKER_FORMAT}
                                value={checkOutPickerValue}
                                onChange={(value) => handleDateChange(value, 'checkOutDate')}
                                disabled={loadingBill}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small',
                                    },
                                }}
                                sx={{ width: '100%' }}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px' }}>
                        <div style={{ margin: '0.5rem' }}>
                            Rooms:
                            {formData.rooms?.map((room: any, key: number) =>
                                !!room && (
                                    <Box className="roomListMulti" key={`${room?.roomType}-${key}`}>
                                        <ButtonGroup
                                            size="small"
                                            aria-label="Small button group"
                                            className="formWrapColl roomsList"
                                        >
                                            <FormControl sx={{ width: '200px' }}>
                                                <InputLabel>Room Type</InputLabel>
                                                <Select
                                                    value={room?.roomCost}
                                                    onChange={(e) => handleChange(e as any, key)}
                                                    name="roomCost"
                                                    disabled={loadingBill}
                                                >
                                                    {roomTypeMenuItems}
                                                </Select>
                                                {showError.roomType && (
                                                    <Typography
                                                        style={{ fontSize: '0.8rem', margin: '0 14px' }}
                                                        color="error"
                                                    >
                                                        {showError.roomType}
                                                    </Typography>
                                                )}
                                            </FormControl>
                                            <TextField
                                                sx={{ width: '140px' }}
                                                className="input-type"
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    handleUpdateRooms(e, key)
                                                }
                                                name="roomCustomCost"
                                                value={room.roomCustomCost}
                                                label="Room Cost"
                                                type="number"
                                                disabled={loadingBill}
                                            />
                                            <TextField
                                                sx={{ width: '140px' }}
                                                className="input-type"
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    handleUpdateRooms(e, key)
                                                }
                                                name="noOfNights"
                                                value={room.noOfNights}
                                                label="No of Nights"
                                                type="number"
                                                disabled={loadingBill}
                                                InputProps={{ inputProps: { min: 1, step: 1 } }}
                                            />
                                            <Select
                                                sx={{ width: '140px' }}
                                                value={room?.roomNumber || ''}
                                                onChange={(e) => handleChange(e as any, key)}
                                                name="roomNumber"
                                                displayEmpty
                                                disabled={loadingBill}
                                            >
                                                <MenuItem value="">
                                                    <em>Select room</em>
                                                </MenuItem>
                                                {roomNumberMenuItems}
                                            </Select>
                                            <Button
                                                className="btnDelete"
                                                color="error"
                                                onClick={() => removeRooms(key)}
                                                disabled={loadingBill}
                                            >
                                                <DeleteIcon />
                                            </Button>
                                        </ButtonGroup>
                                    </Box>
                                ),
                            )}
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={addRooms}
                                disabled={loadingBill}
                            >
                                + Add New
                            </Button>
                        </div>
                    </Grid>
                    {canUploadGuestImages && (
                        <Grid
                            item
                            xs={12}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                backgroundColor: 'var(--primary-light)',
                                padding: '10px',
                            }}
                        >
                            <FileUpload
                                onFileUpload={handleFileUpload}
                                onFileRemove={handleFileRemove}
                                restaurantId={params?.restaurantId || restaurant?.docId}
                                uoId={formData.uoId}
                                guestName={formData.guestName}
                                existingUrls={formData.identificationFiles || []}
                                maxFiles={4}
                                maxFileSizeMB={5}
                            />
                        </Grid>
                    )}
                </Grid>
            </LocalizationProvider>
            <Grid
                item
                xs={12}
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    backgroundColor: 'var(--primary-light)',
                    padding: '10px',
                }}
            >
                <Button
                    disabled={!isOnline || disableSaveBtn || loadingBill}
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                >
                    Save
                </Button>
                {!isOnline && (
                    <Grid xs={4}>
                        <span className="onlineStatue inline offline">Internet ❌</span>
                    </Grid>
                )}
            </Grid>
        </Dialog>
    );
};

export default React.memo(RoomBillingGuestDialog);
